import { clsx } from "clsx";

type Props = {
  variant?: "primary" | "secondary" | "tertiary";
  size?: "extra-small" | "small" | "medium" | "large";
  noPadding?: boolean;
  icon?: JSX.Element;
  children: React.ReactNode;
  disabled?: boolean;
  locked?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  customIconSize?: string;
  disabledVariant?: "default" | "transparent";
  type?: "button" | "submit" | "reset";
};

const IconSection = ({ icon, size, locked, disabled, customIconSize }) => (
  <div
    className={clsx(
      "my-auto",
      customIconSize
        ? customIconSize
        : size === "small" || size === "medium"
          ? "h-3 w-3"
          : "h-4 w-4",
      locked || disabled ? "text-dark-disabled" : "text-light-primary",
    )}
  >
    {icon}
  </div>
);

const PrimaryButton = ({
  size = "medium",
  icon,
  children,
  disabled,
  locked,
  noPadding,
  onClick,
  customIconSize,
  type = "button",
}: Omit<Props, "variant">) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    className={clsx(
      "flex justify-center", // DOUBLE CHECK THIS
      size === "extra-small" ? "rounded" : "rounded-lg",
      // Padding-x with icon is different from padding without icon
      noPadding
        ? "px-1"
        : icon && (size === "small" || size === "extra-small")
          ? "px-2"
          : !icon && (size === "small" || size === "extra-small")
            ? "px-4"
            : icon && size === "medium"
              ? "px-3"
              : !icon && size === "medium"
                ? "px-4"
                : "px-10",
      size === "small" || size === "extra-small"
        ? "gap-x-1 py-1"
        : size === "medium"
          ? "gap-x-1 py-[5px]"
          : "h-10 w-full gap-x-2",
      locked || disabled
        ? "bg-gray-800"
        : "bg-gray-200 shadow-button hover:bg-gray-100",
    )}
  >
    <div
      className={clsx(
        "my-auto whitespace-nowrap font-medium",
        size === "extra-small"
          ? "text-xs"
          : size === "small"
            ? "text-sm"
            : "text-base",
        locked || disabled ? "text-dark-disabled" : "text-light-primary",
      )}
    >
      {children}
    </div>
    {icon && (
      <IconSection
        icon={icon}
        size={size}
        locked={locked}
        disabled={disabled}
        customIconSize={customIconSize}
      />
    )}
  </button>
);

const SecondaryButton = ({
  size = "medium",
  icon,
  children,
  disabled,
  locked,
  noPadding,
  onClick,
  customIconSize,
  type = "button",
}: Omit<Props, "variant">) => {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        "rounded-[10px] border-b border-gray-200 shadow-button",
        locked || disabled
          ? "text-dark-disabled"
          : "text-secondary hover:text-white",
      )}
    >
      <div
        className={clsx(
          "flex justify-center rounded-t-[10px] rounded-b-[8px]",
          // Padding-x with icon is different from padding without icon
          noPadding
            ? "px-1"
            : icon && size === "small"
              ? "px-2"
              : !icon && size === "small"
                ? "px-4"
                : icon && size === "medium"
                  ? "px-3"
                  : !icon && size === "medium"
                    ? "px-4"
                    : "px-10",
          size === "small"
            ? "gap-x-1 py-1"
            : size === "medium"
              ? "gap-x-1 py-[5px]"
              : "h-10 gap-x-2",
          locked || disabled
            ? "border border-gray-600 bg-none"
            : "border-2 border-secondary-dark bg-gray-875 shadow-button hover:border-gray-100 hover:bg-gray-800",
        )}
      >
        <div
          className={clsx(
            "my-auto translate-y-[1px] whitespace-nowrap font-medium",
            size === "small" ? "text-sm" : "text-base",
          )}
        >
          {children}
        </div>
        {icon && (
          <IconSection
            icon={icon}
            size={size}
            locked={locked}
            disabled={disabled}
            customIconSize={customIconSize}
          />
        )}
      </div>
    </button>
  );
};

const TertiaryButton = ({
  size = "medium",
  icon,
  children,
  disabled,
  locked,
  noPadding,
  onClick,
  customIconSize,
  disabledVariant,
  type = "button",
}: Omit<Props, "variant">) => (
  <button
    type={type}
    disabled={disabled}
    onClick={onClick}
    className={clsx(
      "flex justify-center",
      size === "extra-small" ? "rounded" : "rounded-lg",
      // Padding-x with icon is different from padding without icon

      noPadding
        ? "px-1"
        : icon && (size === "small" || size === "extra-small")
          ? "px-2"
          : !icon && (size === "small" || size === "extra-small")
            ? "px-4"
            : icon && size === "medium"
              ? "px-3"
              : !icon && size === "medium"
                ? "px-4"
                : "px-10",
      size === "small" || size === "extra-small"
        ? "gap-x-1 py-1"
        : size === "medium"
          ? "gap-x-1 py-[5px]"
          : "h-10 w-full gap-x-2",
      locked || (disabled && disabledVariant === "default")
        ? "bg-gray-800"
        : disabled && disabledVariant === "transparent"
          ? "bg-white/10"
          : "bg-primary-blue shadow-button hover:bg-primary-washed-blue",
    )}
  >
    <div
      className={clsx(
        "my-auto whitespace-nowrap font-medium",
        size === "extra-small"
          ? "text-xs"
          : size === "small"
            ? "text-sm"
            : "text-base",
        locked || (disabled && disabledVariant === "default")
          ? "text-dark-disabled"
          : disabled && disabledVariant === "transparent"
            ? "text-gray-300"
            : "text-white",
      )}
    >
      {children}
    </div>
    {icon && (
      <IconSection
        icon={icon}
        size={size}
        locked={locked}
        disabled={disabled}
        customIconSize={customIconSize}
      />
    )}
  </button>
);

export const Button = ({
  type = "button",
  variant = "primary",
  size = "medium",
  icon,
  children,
  disabled,
  locked,
  noPadding,
  onClick,
  customIconSize,
  disabledVariant = "default",
}: Props) => {
  return variant === "primary" ? (
    <PrimaryButton
      type={type}
      size={size}
      icon={icon}
      children={children}
      disabled={disabled}
      locked={locked}
      noPadding={noPadding}
      onClick={onClick}
      customIconSize={customIconSize}
    />
  ) : variant === "secondary" ? (
    <SecondaryButton
      type={type}
      size={size}
      icon={icon}
      children={children}
      disabled={disabled}
      locked={locked}
      noPadding={noPadding}
      onClick={onClick}
      customIconSize={customIconSize}
    />
  ) : (
    <TertiaryButton
      type={type}
      size={size}
      icon={icon}
      children={children}
      disabled={disabled}
      locked={locked}
      noPadding={noPadding}
      onClick={onClick}
      customIconSize={customIconSize}
      disabledVariant={disabledVariant}
    />
  );
};
